import { Attr } from '@mode-switch/model';
import { QueryResult } from '@mode-switch/query';
import { EncField, Encoding } from '@mode-vegas/vegas';
import { ChartDataTypes, HashKeyTypes } from '@mode/shared/contract-common';
import { HashKey } from '@mode/shared/util-js';
import { RenderDataModel } from '../schema-model';
import { SummaryValueFormatter, makeSummaryValueFormatter } from './chart-summary-format';

type ChartColumn = ChartDataTypes.Column;
type ChartDataset = ChartDataTypes.Dataset;

export function makeSummaryData(model: RenderDataModel, encoding: Encoding): ChartDataTypes.Dataset {
  switch (encoding.marks.channel) {
    case '*': {
      return makeTabularSummaryData(model, encoding);
    }
    case 'x': {
      return makeVerticalSummaryData(model, encoding);
    }
    case 'y': {
      return makeHorizontalSummaryData(model, encoding);
    }
  }
}

/**
 *
 * @param model A render model for compiling formulas
 * @param encoding An encoding with the marks.channel value set to '*'
 *
 */
export function makeTabularSummaryData(model: RenderDataModel, encoding: Encoding): ChartDataset {
  const { marks } = encoding;
  const shared = marks.properties;

  const sharedFields: EncField[] = [
    ...encoding.column,
    ...encoding.x,
    ...encoding.x2,
    ...encoding.row,
    ...encoding.y,
    ...encoding.y2,
    ...shared.color.values,
    ...shared.size.values,
    ...shared.text.values,
    ...shared.detail.values,
    ...shared.tooltip.values,
    ...shared.angle.values,
    ...shared.baseline.values,
  ];

  const schema = model.getSeriesSchema('*');
  const dataset = model.getSeriesData('*');

  if (!hasValidSchemaForFields(schema, sharedFields)) {
    return makeEmptyDataset();
  }

  const seriesFields: EncField[] = [];
  const uniqueFields: Array<EncField[]> = [];

  const schemas: Array<ChartColumn[]> = [schema];
  const datasets: Array<QueryResult.Item[]> = [dataset as QueryResult.Item[]];

  return makeCombinedDataset(model, sharedFields, seriesFields, uniqueFields, schemas, datasets);
}

/**
 *
 * @param model A render model for compiling formulas
 * @param encoding An encoding with the marks.channel value set to 'x'
 *
 */
export function makeVerticalSummaryData(model: RenderDataModel, encoding: Encoding): ChartDataset {
  const { marks } = encoding;

  const sharedFields: EncField[] = [...encoding.row, ...encoding.y, ...encoding.y2, ...encoding.column];

  const seriesFields: EncField[] = [];
  const uniqueFields: Array<EncField[]> = [];

  const schemas: Array<ChartColumn[]> = [];
  const datasets: Array<QueryResult.Item[]> = [];

  for (const series of marks.series) {
    let xField =
      encoding.x.find((field) => field.id == series.field) || encoding.x2.find((field) => field.id == series.field);

    if (xField !== undefined) {
      seriesFields.push(xField);
    } else {
      throw new Error(`Cannot find x series field ${series.field}`);
    }

    const uniqueSeriesFields = [
      ...series.properties.color.values,
      ...series.properties.size.values,
      ...series.properties.text.values,
      ...series.properties.detail.values,
      ...series.properties.tooltip.values,
      ...series.properties.angle.values,
      ...series.properties.baseline.values,
    ];

    uniqueFields.push(uniqueSeriesFields);

    const schema = model.getSeriesSchema(series.field);
    const dataset = model.getSeriesData(series.field);

    if (!hasValidSchemaForFields(schema, [...sharedFields, xField, ...uniqueSeriesFields])) {
      return makeEmptyDataset();
    }

    schemas.push(schema);
    datasets.push(dataset as QueryResult.Item[]);
  }

  return makeCombinedDataset(model, sharedFields, seriesFields, uniqueFields, schemas, datasets);
}

/**
 *
 * @param model A render model for compiling formulas
 * @param encoding An encoding with the marks.channel value set to 'y'
 *
 */
export function makeHorizontalSummaryData(model: RenderDataModel, encoding: Encoding): ChartDataset {
  const { marks } = encoding;

  const sharedFields: EncField[] = [...encoding.column, ...encoding.x, ...encoding.x2, ...encoding.row];

  const seriesFields: EncField[] = [];
  const uniqueFields: Array<EncField[]> = [];

  const schemas: Array<ChartColumn[]> = [];
  const datasets: Array<QueryResult.Item[]> = [];

  for (const series of marks.series) {
    const yField =
      encoding.y.find((field) => field.id == series.field) || encoding.y2.find((field) => field.id == series.field);

    if (yField !== undefined) {
      seriesFields.push(yField);
    } else {
      throw new Error(`Cannot find y series field ${series.field}`);
    }

    const uniqueSeriesFields = [
      ...series.properties.color.values,
      ...series.properties.size.values,
      ...series.properties.text.values,
      ...series.properties.detail.values,
      ...series.properties.tooltip.values,
      ...series.properties.angle.values,
      ...series.properties.baseline.values,
    ];

    uniqueFields.push(uniqueSeriesFields);

    const schema = model.getSeriesSchema(series.field);
    const dataset = model.getSeriesData(series.field);

    if (!hasValidSchemaForFields(schema, [...sharedFields, yField, ...uniqueSeriesFields])) {
      return makeEmptyDataset();
    }

    schemas.push(schema);
    datasets.push(dataset as QueryResult.Item[]);
  }

  return makeCombinedDataset(model, sharedFields, seriesFields, uniqueFields, schemas, datasets);
}

/**
 *
 * The next two functions are a workaround to a poorly designed editor state machine.
 *
 * The selectors that end up calling this function don't model the state machine
 * completely which can causes the summary data method be called with an encoding
 * and a set of query results that don't match. At the same time model#getSeriesSchema
 * method returns an empty array when it doesn't find the series we asked for instead of
 * undefined.
 *
 * These two issues together mean that we have to go back and validate the schema matches
 *  the encoding exactly or else we can't guarantee that the rest of our methods will have
 *  the values they need to operate correctly. In the case where things don't match we return
 *  an empty dataset rather than throw an error because our current selector setup will *always*
 *  call summary data with invalid inputs when switching tabs in the user interface.
 *
 */
function makeEmptyDataset(): ChartDataset {
  return {
    count: 0,
    content: [],
    columns: [],
    state: ChartDataTypes.Dataset.State.Succeeded,
  };
}

function hasValidSchemaForFields(schema: ChartColumn[], fields: Array<EncField>): boolean {
  if (schema.length === 0 && fields.length > 0) {
    return false;
  }

  for (const field of fields) {
    const fieldId = field.id || field.formula;
    const fieldColumn = schema.find((column) => column.name === fieldId);

    if (fieldColumn === undefined) {
      return false;
    }
  }
  return true;
}

/**
 *
 * Creates a shared dataset for displaying in the summary table
 *
 * This method converts the provided datasets into a single data set
 * suitable for display in a summary table.
 *
 * If there is only one series, the returned data contains the same number
 * of rows as the provided data. The data is only tranformed to include the
 * proper column header names, etc.
 *
 * If there are multiple series, then the data is combined into a data set which
 * includes appropriate columns and rows from all of the series. If all of the
 * series have common shared channel field (or no channel fields), then the returned
 * data will include the same number of rows as the original datasets. The datasets will
 * essentially be combined into a single wide dataset with the extra series fields
 * added as additional columns. If the channels are different between the series, then
 * the combined table will be tall (i.e. contain more rows). The rows from each dataset
 * that aren't common will be appended to the bottom each dataset.
 *
 * @param model - A model that can be used to compile fields
 * @param sharedFields - The set of fields that are shared among all series
 * @param seriesFields - The set of series fields corresponding with each dataset
 * @param uniqueFields - The set of unique (mark) fields within each series dataset
 * @param schemas - The column information for the query result corresponding to this series
 * @param datasets - The datasets for the query result corresponding to this series
 *
 */
function makeCombinedDataset(
  model: RenderDataModel,
  sharedFields: Array<EncField>,
  seriesFields: Array<EncField>,
  uniqueFields: Array<EncField[]>,
  schemas: Array<ChartColumn[]>,
  datasets: Array<QueryResult.Item[]>
) {
  // EncFieldId => ColumnHeader
  const fieldMap = new Map<string, string>();
  // ColumnHeader => ChartColumn
  const columnMap = new Map<string, ChartColumn>();
  // ColumnHeader => AttrRole
  const columnRoleMap = new Map<string, Attr.Role>();
  // EncFieldId => SummaryValueFormatter
  const valueFormatterMap = new Map<string, SummaryValueFormatter>();
  // The template includes all of the fields that will be in the final
  // data with 'null' values. It is used to make sure that all rows
  // include all column values.
  const templateItem: any = {};

  // Resolve any header name conflicts. Conflicts occur when we use a shortened default name
  // for a field (ex: 'orders' in place of 'SUM(orders)') that matches a discrete field name.
  resolveHeaderNameConflicts(model, sharedFields, seriesFields, uniqueFields, schemas);

  const addFieldMetadata = (schema: ChartColumn[], field: EncField) => {
    if (schema === undefined) {
      throw new Error(`Cannot build dataset without at least one schema`);
    }
    const sharedColumn = findColumnInSchema(schema, field);
    if (sharedColumn === undefined) {
      throw new Error(`Cannot find column for shared field ${field.formula}`);
    } else {
      insertFieldInMetadata(
        model,
        field,
        sharedColumn,
        fieldMap,
        columnMap,
        columnRoleMap,
        valueFormatterMap,
        templateItem
      );
    }
  };

  // Build out some metadata about all of the fields to be included in the data from the encoding fields.
  for (const sharedField of sharedFields) {
    const schema = schemas[0];
    addFieldMetadata(schema, sharedField);
  }

  seriesFields.forEach((seriesField, index) => {
    const schema = schemas[index];
    addFieldMetadata(schema, seriesField);

    for (const uniqueField of uniqueFields[index]) {
      addFieldMetadata(schema, uniqueField);
    }
  });
  /**
   * For each series, the data objects are stored in a map by the hash key of the shared
   * fields and unique fields. Since it is possible for multiple rows to hash to the same
   * key (ex non-aggregated continuous), the data objects are stored in arrays. We never
   * want to combine values within a series, only across series.
   */
  const allSeriesData = new Array<Map<number, Array<any>>>();
  // Keep all of the hash keys for building the final data.
  const allHashKeys = new Set<number>();

  if (seriesFields.length === 0) {
    const hashKeyFn = makeHashKeyFn(sharedFields);
    for (const dataset of datasets) {
      const seriesData = new Map<number, Array<any>>();
      for (const record of dataset) {
        const hashKey = hashKeyFn(record);
        allHashKeys.add(hashKey);

        let items = seriesData.get(hashKey);
        if (items === undefined) {
          items = [];
          seriesData.set(hashKey, items);
        }

        const item = {};
        for (const sharedField of sharedFields) {
          copyRecordValueIntoItem(sharedField, fieldMap, valueFormatterMap, record, item);
        }
        items.push(item);
      }
      allSeriesData.push(seriesData);
    }
  } else {
    seriesFields.forEach((field, index) => {
      const seriesData = new Map<number, Array<any>>();
      let dataset = datasets[index];
      let uniqueSeriesFields = uniqueFields[index];
      // The hash key needs to unique across both the shared fields and the channel fields
      const hashKeyFn = makeHashKeyFn([...sharedFields, ...uniqueSeriesFields]);

      for (const record of dataset) {
        const hashKey = hashKeyFn(record);
        allHashKeys.add(hashKey);

        let items = seriesData.get(hashKey);
        if (items === undefined) {
          items = [];
          seriesData.set(hashKey, items);
        }

        const item = {};
        copyRecordValueIntoItem(field, fieldMap, valueFormatterMap, record, item);

        for (const sharedField of sharedFields) {
          copyRecordValueIntoItem(sharedField, fieldMap, valueFormatterMap, record, item);
        }

        for (const uniqueSeriesField of uniqueSeriesFields) {
          copyRecordValueIntoItem(uniqueSeriesField, fieldMap, valueFormatterMap, record, item);
        }
        items.push(item);
      }
      allSeriesData.push(seriesData);
    });
  }
  /**
   * The allSeriesData array contains the data for each series. Combine these together based on the
   * hash key since a matching hash means that the shared values match. Ex:
   * Series 1: key (1000) => { region: 'North', color: 'Blue', price: '18.2'}
   * Series 2: key (1000) => { region: 'North', color: 'Blue', cost: '9.8'}
   * Series 3: key (1000) => { region: 'North', color: 'Blue', profit: '5.5'}
   *
   * Result:
   * {region: 'North', color: 'Blue', price: '18.2, cost: '9.8', profit: '5.5' }
   *
   * There may be more keys than rows in the original datasets if channels
   * are different between series. Ex:
   * Series 1: key (1000) => { region: 'North', color: 'Blue', price: '18.2'}
   * Series 2: key (1001) => { region: 'North', color: 'Red', cost: '9.8'}
   * Series 3: key (1002) => { region: 'North', color: 'Green', profit: '5.5'}
   *
   * Result:
   * {region: 'North', color: 'Blue', price: '18.2, cost: null, profit: null }
   * {region: 'North', color: 'Red', price: null, cost: '9.8', profit: null }
   * {region: 'North', color: 'Green', price: null, cost: null, profit: '5.5' }
   *
   */
  const content: Array<any> = [];
  allHashKeys.forEach((hash) => {
    let mergedData: Array<any> = [];
    allSeriesData.forEach((series) => {
      const seriesData = series.get(hash) ?? [];
      mergedData = mergeDataItems(mergedData, seriesData);
    });
    // After the individual items have been merged, it's possible that the
    // merged object won't contain all of the fields. Combine with the template
    // object so fill in the empty fields with 'null'.
    mergedData.forEach((merged) => {
      content.push({ ...templateItem, ...merged });
    });
  });

  // Final column list
  const columns = new Array<ChartColumn>();
  for (const [columnName, column] of Array.from(columnMap.entries())) {
    // Set column names to "pretty" name
    columns.push({ ...column, name: columnName });
  }

  // Final column sorting
  sortSummaryColumns(schemas, columns, columnRoleMap);

  return {
    content,
    columns,
    count: content.length,
    state: ChartDataTypes.Dataset.State.Succeeded,
  };
}

/**
 * Merges two arrays of data objects into a single array of
 * items with combined fields/values.
 * @param series1 Array of data objects
 * @param series2 Array of data objects
 * @returns Array of data objects which combine the fields/values
 * from the input arrays. The length of the returned array will
 * be the max length of the input arrays.
 */
function mergeDataItems(series1: any[], series2: any[]) {
  // Function to ensure that the two arrays are the same size
  const balanceArrays = (full: any[], partial: any[]) => {
    if (full.length === partial.length) {
      return;
    }
    while (full.length > partial.length) {
      partial.push({});
    }
  };

  const primary = series1.length >= series2.length ? series1 : series2;
  const secondary = series1.length < series2.length ? series1 : series2;
  balanceArrays(primary, secondary);

  return primary.map((primaryItem, index) => {
    const mergeItem = secondary[index] ?? {};
    return {
      ...mergeItem,
      ...primaryItem,
    };
  });
}

function findColumnInSchema(columns: ChartColumn[], field: EncField): ChartColumn | undefined {
  return columns.find((column) => column.name === (field.id || field.formula));
}

/**
 *
 * Creates a generic hash key function that is used by
 *  the join probe to allocate find which existing item
 *  to assign a new set of item fields into.
 *
 */
function makeHashKeyFn(fields: EncField[]): HashKeyTypes.HashFn {
  return HashKey.unkeyed(fields.map((f) => f.id ?? f.formula));
}

function insertFieldInMetadata(
  model: RenderDataModel,
  field: EncField,
  schemaColumn: ChartColumn,
  fieldMap: Map<string, string>,
  columnMap: Map<string, ChartColumn>,
  columnRoleMap: Map<string, Attr.Role>,
  formatterMap: Map<string, SummaryValueFormatter>,
  templateItem: any
): void {
  if (schemaColumn !== undefined) {
    const fieldId = field.id || field.formula;
    const fieldName = schemaColumn.header || fieldId;

    if (!fieldMap.has(fieldId)) {
      fieldMap.set(fieldId, fieldName);
      columnMap.set(fieldName, schemaColumn);

      const attr = model.makeField(field);

      if (attr === undefined) {
        throw new Error(`Cannot compile field ${field.formula}`);
      }

      columnRoleMap.set(fieldName, attr.role);
      formatterMap.set(fieldId, makeSummaryValueFormatter(attr));

      templateItem[schemaColumn.header || fieldId] = null;
    }
  } else {
    throw new Error(`Cannot find column for field ${field.formula}`);
  }
}

function copyRecordValueIntoItem(
  field: EncField,
  fieldMap: Map<string, string>,
  formatterMap: Map<string, SummaryValueFormatter>,
  record: QueryResult.Item,
  item: any
): void {
  const fieldId = field.id || field.formula;
  const fieldFormatter = formatterMap.get(fieldId);

  if (fieldFormatter === undefined) {
    item[fieldMap.get(fieldId) || fieldId] = record[fieldId];
  } else {
    item[fieldMap.get(fieldId) || fieldId] = fieldFormatter(record[fieldId]);
  }
}

/**
 *
 * Sorts columns in the summary by prioritizing (left to right) columns based on role:
 *
 * 1. Dimensions
 * 2. Measures
 *
 * Within each those groups we sort fields by prioritizing (left to right) columns that occur
 *  the most frequently across all of the schemas that are returned in our helix query results.
 *
 * @param schemas Set of all schemas from all query results
 * @param columns Set of all of the columns from all query results
 * @param attrRoleMap Map of attribute roles keyed by field id for all fields
 */
function sortSummaryColumns(
  schemas: Array<ChartColumn[]>,
  columns: ChartColumn[],
  attrRoleMap: Map<string, Attr.Role>
): void {
  const columnFreq = new Map<string, number>();

  for (const schema of schemas) {
    for (const column of schema) {
      const fieldName = column.header || column.name;
      columnFreq.set(fieldName, (columnFreq.get(fieldName) || 0) + 1);
    }
  }

  columns.sort((lhs, rhs) => {
    const lhsKey = lhs.header || lhs.name;
    const rhsKey = rhs.header || rhs.name;

    const lhsFreqRank = columnFreq.get(lhsKey) || 0;
    const rhsFreqRank = columnFreq.get(rhsKey) || 0;

    let lhsTypeRank = 1;
    let rhsTypeRank = 1;

    if (attrRoleMap.get(lhsKey) === Attr.Role.Dimension) {
      lhsTypeRank = 0;
    }

    if (attrRoleMap.get(rhsKey) === Attr.Role.Dimension) {
      rhsTypeRank = 0;
    }

    if (lhsTypeRank < rhsTypeRank) {
      return -1;
    }

    if (lhsTypeRank > rhsTypeRank) {
      return 1;
    }

    // Reverse frequency ranking

    if (rhsFreqRank < lhsFreqRank) {
      return -1;
    }

    if (lhsFreqRank > rhsFreqRank) {
      return 1;
    }

    return 0;
  });
}

/**
 * Updates the schemas if there are header naming conflicts. A conflict occurs when
 * two fields have the same query column name but differ in display formula. It is
 * acceptable for two fields to have the same name if they have the same formula
 * because the values are the same. In that case, using the same header name prevents
 * duplicate columns in the summary table.
 *
 * If there is a duplicate/conflict header name, the schema for the fields are
 * changed to use the display formula. For discrete fields, the display formula
 * is the same as the header name so there is no change. Continuous fields will
 * display the aggregation.
 */
function resolveHeaderNameConflicts(
  model: RenderDataModel,
  sharedFields: Array<EncField>,
  seriesFields: Array<EncField>,
  uniqueFields: Array<EncField[]>,
  schemas: Array<ChartColumn[]>
) {
  if (seriesFields.length > 0) {
    // For series, we need to first resolve any conflicts within the series
    // i.e. the shared, series and channel fields for each series.
    seriesFields.forEach((series, index) => {
      resolveFieldHeaderNameConflicts(model, [[...sharedFields, series, ...uniqueFields[index]]], [schemas[index]]);
    });
    // Then resolve any conflicts across all the series channel fields
    resolveFieldHeaderNameConflicts(model, uniqueFields, schemas);
  } else {
    // For tabular data, all of the fields are shared fields
    resolveFieldHeaderNameConflicts(model, [sharedFields], schemas);
  }
}

function resolveFieldHeaderNameConflicts(
  model: RenderDataModel,
  fieldArrays: Array<EncField[]>,
  schemas: Array<ChartColumn[]>
) {
  // Build a map of header name to field(s)
  const headerToEncFieldMap = buildHeaderToFieldMap(fieldArrays, schemas);
  // Look for any names which are common to multiple fields.
  Array.from(headerToEncFieldMap.values()).forEach((encFields) => {
    if (encFields.length > 1) {
      //
      // Potential name collision. Build an array of alternate names
      // based on the display formula of the field.
      //  - For discrete fields, the alt-name is the same as the header name.
      //  - For continuous fields, we get name conflicts when the default
      //    aggregation is dropped (Ex: 'SUM(order_gloss_qty)' becomes 'order_gloss_qty').
      //    The alt-name adds back the aggregation
      //  - For DateTrunc, we can have conflicts with DatePart. Here we add in
      //    'DATETRUNC' to disambiguate. This is a fairly rare use case.
      //
      const altNames = encFields.map(({ field }) => {
        const compiledColumn = model.compileEncField(field);
        return compiledColumn.hasDateTrunc
          ? `DATETRUNC ${compiledColumn.displayFormula}`
          : compiledColumn.displayFormula;
      });
      // We only need to update the schema to use the altNames if all of the
      // alt-names are not the same. If they are all the same, then they all
      // have the same value and can be collapsed into one entry.
      if (!altNames.every((altName) => altName === altNames[0])) {
        // Update the schema to use the alternate names
        encFields.forEach(({ field, seriesIndex }, index) => {
          const schema = schemas[seriesIndex];
          const schemaItemIndex = schema.findIndex((item) => item.name === field.id);
          if (schemaItemIndex >= 0) {
            schema[schemaItemIndex] = {
              ...schema[schemaItemIndex],
              header: altNames[index],
            };
          }
        });
      }
    }
  });
}

/**
 * Builds a mapping for header name to field. This is used to find potential
 * conflicts.
 */
function buildHeaderToFieldMap(fieldArrays: Array<EncField[]>, schemas: Array<ChartColumn[]>) {
  const headerToEncFieldMap = new Map<string, Array<{ field: EncField; seriesIndex: number }>>();
  fieldArrays.forEach((fieldArray, seriesIndex) => {
    const schema = schemas[seriesIndex];
    fieldArray.forEach((field) => {
      const schemaItem = schema.find((column) => column.name === field.id);
      if (schemaItem) {
        const header = schemaItem.header ?? '';
        if (headerToEncFieldMap.has(header)) {
          headerToEncFieldMap.get(header)?.push({ field, seriesIndex });
        } else {
          headerToEncFieldMap.set(header, [{ field, seriesIndex }]);
        }
      }
    });
  });
  return headerToEncFieldMap;
}
